import axios from 'axios';

// Prefer base URL without "/api" to avoid double prefixing paths
const API_URL =
  (import.meta.env as any).VITE_API_BASE_URL ||
  (import.meta.env as any).VITE_API_URL ||
  `\${API_BASE_URL}`;

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Axios instance with auth
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface ApiKey {
  id: string;
  userId: string;
  name: string;
  key: string;
  secret?: string;
  scopes: string[];
  rateLimit: number;
  requestCount: number;
  lastUsedAt?: string;
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    usageLogs: number;
  };
}

export interface ApiScope {
  value: string;
  label: string;
  description: string;
}

export interface ApiScopes {
  read: ApiScope[];
  write: ApiScope[];
  admin: ApiScope[];
}

export interface CreateApiKeyInput {
  name: string;
  scopes: string[];
  rateLimit?: number;
  expiresAt?: string;
}

export interface UpdateApiKeyInput {
  name?: string;
  scopes?: string[];
  rateLimit?: number;
  isActive?: boolean;
  expiresAt?: string;
}

export interface ApiKeyUsageStats {
  totalRequests: number;
  last24Hours: number;
  last7Days: number;
  last30Days: number;
  topEndpoints: Array<{ endpoint: string; count: number }>;
  statusCodes: Record<string, number>;
  averageResponseTime: number;
}

export interface ApiUsageLog {
  id: string;
  apiKeyId: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number;
  ipAddress?: string;
  userAgent?: string;
  error?: string;
  createdAt: string;
}

export interface RateLimitInfo {
  allowed: boolean;
  remaining: number;
  resetAt: string;
}

// API Key Management Service
class ApiKeyService {
  private mockEnabled = (import.meta.env as any).VITE_USE_MOCK_API_KEYS === 'true';
  private mockKeys: ApiKey[] = [];

  private simulate<T>(data: T, delay = 250): Promise<T> {
    return new Promise(resolve => setTimeout(() => resolve(data), delay));
  }

  /**
   * Get available API scopes
   */
  async getScopes(): Promise<ApiScopes> {
    if (this.mockEnabled) {
      return this.simulate({
        read: [
          { value: 'read:users', label: 'Read Users', description: 'View user profiles' },
          { value: 'read:posts', label: 'Read Posts', description: 'View posts and content' }
        ],
        write: [
          { value: 'write:posts', label: 'Write Posts', description: 'Create and edit posts' }
        ],
        admin: [
          { value: 'admin:api_keys', label: 'Manage API Keys', description: 'Full API key administration' }
        ]
      });
    }
    const response = await api.get('/api/api-keys/scopes');
    return response.data;
  }

  /**
   * Get all API keys for the current user
   */
  async getApiKeys(): Promise<ApiKey[]> {
    if (this.mockEnabled) {
      // Seed a mock key if empty
      if (!this.mockKeys.length) {
        this.mockKeys.push({
          id: 'mock1',
          userId: 'user_mock',
          name: 'Demo Key',
            key: 'pk_mockdemo_1234567890abcdef',
          secret: undefined,
          scopes: ['read:users', 'read:posts'],
          rateLimit: 1000,
          requestCount: 42,
          lastUsedAt: new Date().toISOString(),
          isActive: true,
          expiresAt: undefined,
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          updatedAt: new Date().toISOString(),
          _count: { usageLogs: 10 }
        });
      }
      return this.simulate(this.mockKeys);
    }
    const response = await api.get('/api/api-keys');
    return response.data;
  }

  /**
   * Get a specific API key
   */
  async getApiKey(apiKeyId: string): Promise<ApiKey> {
    const response = await api.get(`/api/api-keys/${apiKeyId}`);
    return response.data;
  }

  /**
   * Create a new API key
   */
  async createApiKey(data: CreateApiKeyInput): Promise<ApiKey> {
    if (this.mockEnabled) {
      const mock: ApiKey = {
        id: 'mock_' + Math.random().toString(36).slice(2, 10),
        userId: 'user_mock',
        name: data.name,
        key: 'pk_' + Math.random().toString(16).slice(2).padEnd(32, '0'),
        secret: 'sk_' + Math.random().toString(16).slice(2).padEnd(48, '0'),
        scopes: data.scopes,
        rateLimit: data.rateLimit || 1000,
        requestCount: 0,
        lastUsedAt: undefined,
        isActive: true,
        expiresAt: data.expiresAt,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _count: { usageLogs: 0 }
      };
      this.mockKeys.push(mock);
      return this.simulate(mock);
    }
    const response = await api.post('/api/api-keys', data);
    return response.data;
  }

  /**
   * Update an API key
   */
  async updateApiKey(apiKeyId: string, data: UpdateApiKeyInput): Promise<ApiKey> {
    const response = await api.put(`/api/api-keys/${apiKeyId}`, data);
    return response.data;
  }

  /**
   * Delete an API key
   */
  async deleteApiKey(apiKeyId: string): Promise<void> {
    if (this.mockEnabled) {
      this.mockKeys = this.mockKeys.filter(k => k.id !== apiKeyId);
      return this.simulate(undefined as any);
    }
    await api.delete(`/api/api-keys/${apiKeyId}`);
  }

  /**
   * Revoke an API key (mark as inactive)
   */
  async revokeApiKey(apiKeyId: string): Promise<ApiKey> {
    if (this.mockEnabled) {
      const key = this.mockKeys.find(k => k.id === apiKeyId);
      if (key) {
        key.isActive = false;
        key.updatedAt = new Date().toISOString();
      }
      return this.simulate(key!);
    }
    const response = await api.put(`/api/api-keys/${apiKeyId}/revoke`);
    return response.data.apiKey;
  }

  /**
   * Rotate API key secret
   */
  async rotateSecret(apiKeyId: string): Promise<{ key: string; secret: string; message: string }> {
    if (this.mockEnabled) {
      const key = this.mockKeys.find(k => k.id === apiKeyId);
      if (key) {
        const newSecret = 'sk_' + Math.random().toString(16).slice(2).padEnd(48, '0');
        key.secret = newSecret; // Exposed once
        key.updatedAt = new Date().toISOString();
        return this.simulate({ key: key.key, secret: newSecret, message: 'Secret rotated (mock)' });
      }
      return this.simulate({ key: '', secret: '', message: 'Not found' });
    }
    const response = await api.post(`/api/api-keys/${apiKeyId}/rotate-secret`);
    return response.data;
  }

  /**
   * Get usage statistics for an API key
   */
  async getUsageStats(apiKeyId: string): Promise<ApiKeyUsageStats> {
    if (this.mockEnabled) {
      return this.simulate({
        totalRequests: 420,
        last24Hours: 120,
        last7Days: 840,
        last30Days: 3000,
        topEndpoints: [
          { endpoint: '/api/posts', count: 180 },
          { endpoint: '/api/memberships', count: 90 }
        ],
        statusCodes: { '200': 380, '400': 20, '500': 20 },
        averageResponseTime: 180
      });
    }
    const response = await api.get(`/api/api-keys/${apiKeyId}/stats`);
    return response.data;
  }

  /**
   * Get usage logs for an API key
   */
  async getUsageLogs(
    apiKeyId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ logs: ApiUsageLog[]; total: number; limit: number; offset: number }> {
    if (this.mockEnabled) {
      const now = Date.now();
      const logs: ApiUsageLog[] = Array.from({ length: Math.min(10, limit) }).map((_, i) => ({
        id: `log_${i}`,
        apiKeyId,
        endpoint: i % 2 === 0 ? '/api/posts' : '/api/memberships',
        method: i % 3 === 0 ? 'POST' : 'GET',
        statusCode: i % 5 === 0 ? 500 : 200,
        responseTime: 100 + i * 7,
        ipAddress: '127.0.0.1',
        userAgent: 'MockAgent/1.0',
        error: i % 5 === 0 ? 'Internal Server Error' : undefined,
        createdAt: new Date(now - i * 60000).toISOString(),
      }));
      return this.simulate({ logs, total: 10, limit, offset });
    }
    const response = await api.get(`/api/api-keys/${apiKeyId}/logs`, {
      params: { limit, offset },
    });
    return response.data;
  }

  /**
   * Check rate limit for an API key
   */
  async checkRateLimit(apiKeyId: string): Promise<RateLimitInfo> {
    if (this.mockEnabled) {
      return this.simulate({ allowed: true, remaining: 875, resetAt: new Date(Date.now() + 3600_000).toISOString() });
    }
    const response = await api.get(`/api/api-keys/${apiKeyId}/rate-limit`);
    return response.data;
  }
}

// Helper functions
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

export const getScopeCategory = (scope: string): 'read' | 'write' | 'admin' | 'unknown' => {
  if (scope.startsWith('read:')) {
    return 'read';
  } else if (scope.startsWith('write:')) {
    return 'write';
  } else if (scope.startsWith('admin:')) {
    return 'admin';
  }
  return 'unknown';
};

export const getScopeCategoryColor = (category: 'read' | 'write' | 'admin' | 'unknown'): string => {
  switch (category) {
    case 'read':
      return 'text-blue-600 bg-blue-50';
    case 'write':
      return 'text-orange-600 bg-orange-50';
    case 'admin':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

export const getStatusColor = (statusCode: number): string => {
  if (statusCode >= 200 && statusCode < 300) {
    return 'text-green-600 bg-green-50';
  } else if (statusCode >= 300 && statusCode < 400) {
    return 'text-blue-600 bg-blue-50';
  } else if (statusCode >= 400 && statusCode < 500) {
    return 'text-orange-600 bg-orange-50';
  } else {
    return 'text-red-600 bg-red-50';
  }
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

export default new ApiKeyService();
